import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Clock, CheckCircle2, XCircle, FileText, ShieldCheck, Building2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminKyc() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, all: 0 });
  const [filter, setFilter] = useState("pending");
  const [notes, setNotes] = useState({});
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    if (user.role !== "admin") {
      toast.error("Accès réservé aux administrateurs");
      navigate("/dashboard");
      return;
    }
    refresh();
  }, [user, loading, navigate, filter]);

  const refresh = async () => {
    try {
      const [{ data: list }, { data: c }] = await Promise.all([
        api.get("/admin/kyc", { params: filter === "all" ? {} : { status: filter } }),
        api.get("/admin/kyc/counts"),
      ]);
      setItems(list);
      setCounts(c);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const review = async (kycId, decision) => {
    setBusyId(kycId);
    try {
      await api.post(`/admin/kyc/${kycId}/review`, { decision, notes: notes[kycId] || null });
      toast.success(decision === "approved" ? "Dossier validé ✓" : "Dossier refusé");
      setNotes({ ...notes, [kycId]: "" });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <Layout withFooter={false}>
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8">
        <span className="text-xs tracking-[0.2em] uppercase text-[#11783A] font-medium">Admin</span>
        <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight mt-2 mb-2 flex items-center gap-2">
          Validation KYC <ShieldCheck className="w-8 h-8 text-[#11783A]" />
        </h1>
        <p className="text-muted-foreground mb-8">Examinez les dossiers Pro soumis par les agences.</p>

        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            ["pending", "En attente", Clock, "#F4B400"],
            ["approved", "Validés", CheckCircle2, "#11783A"],
            ["rejected", "Refusés", XCircle, "#D6202C"],
            ["all", "Tous", FileText, "#9a9a9a"],
          ].map(([k, label, Icon, color]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              data-testid={`admin-filter-${k}`}
              className={`px-4 h-10 rounded-full text-sm font-medium border transition flex items-center gap-1.5 ${
                filter === k ? "border-transparent text-white" : "bg-white border-[#E5E5E5] hover:bg-[#FAFAFA]"
              }`}
              style={filter === k ? { background: color } : {}}
            >
              <Icon className="w-4 h-4 -mt-0.5" /> {label}
              <span
                data-testid={`admin-count-${k}`}
                className={`text-[11px] tabular-nums px-1.5 py-0.5 rounded-full ${
                  filter === k ? "bg-white/25" : "bg-[#F5F0EB] text-muted-foreground"
                }`}
              >
                {counts[k] ?? 0}
              </span>
            </button>
          ))}
        </div>

        {items.length === 0 ? (
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-12 text-center text-muted-foreground">
            Aucun dossier {filter !== "all" ? `« ${filter} »` : ""}.
          </div>
        ) : (
          <div className="space-y-5" data-testid="admin-kyc-list">
            {items.map((k) => (
              <div key={k.id} className="bg-white border border-[#E5E5E5] rounded-2xl p-5">
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="w-12 h-12 rounded-full bg-[#11783A]/10 text-[#11783A] flex items-center justify-center font-heading text-lg">
                    {k.user?.name?.charAt(0) || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-heading text-lg font-medium">{k.user?.name || "—"}</div>
                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                      <Building2 className="w-3.5 h-3.5" /> {k.user?.company || "Agence non précisée"} · {k.user?.country}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">{k.user?.email} · soumis {new Date(k.submitted_at).toLocaleDateString("fr-FR")}</div>
                  </div>
                  <StatusPill status={k.status} />
                </div>

                <div className="flex gap-3 mt-4 flex-wrap">
                  <a
                    href={`${BACKEND_URL}${k.rc_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-[#D6202C] hover:underline"
                  >
                    <FileText className="w-4 h-4" /> Registre du Commerce
                  </a>
                  <a
                    href={`${BACKEND_URL}${k.tax_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-[#D6202C] hover:underline"
                  >
                    <FileText className="w-4 h-4" /> Attestation fiscale
                  </a>
                </div>

                {k.notes && <p className="text-sm text-muted-foreground mt-3 italic">Notes du Pro : « {k.notes} »</p>}

                {k.status === "pending" && (
                  <>
                    <Textarea
                      placeholder="Notes pour le Pro (optionnel, requis si refus)"
                      value={notes[k.id] || ""}
                      onChange={(e) => setNotes({ ...notes, [k.id]: e.target.value })}
                      rows={2}
                      className="rounded-xl mt-4"
                      data-testid={`admin-review-notes-${k.id}`}
                    />
                    <div className="flex gap-2 mt-3">
                      <Button
                        onClick={() => review(k.id, "approved")}
                        disabled={busyId === k.id}
                        data-testid={`admin-approve-${k.id}`}
                        className="rounded-full bg-[#11783A] hover:bg-[#0A5C2C]"
                      >
                        <CheckCircle2 className="w-4 h-4 me-1" /> Valider
                      </Button>
                      <Button
                        onClick={() => review(k.id, "rejected")}
                        disabled={busyId === k.id}
                        variant="outline"
                        data-testid={`admin-reject-${k.id}`}
                        className="rounded-full text-[#D6202C] border-[#D6202C] hover:bg-[#FEF2F3]"
                      >
                        <XCircle className="w-4 h-4 me-1" /> Refuser
                      </Button>
                    </div>
                  </>
                )}

                {k.status !== "pending" && k.review_notes && (
                  <p className="text-xs mt-3 px-3 py-2 rounded-xl bg-[#FAFAFA] border border-[#E5E5E5]">
                    <strong>Décision :</strong> {k.review_notes}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

const StatusPill = ({ status }) => {
  const meta = {
    pending: { color: "#F4B400", label: "En attente", Icon: Clock },
    approved: { color: "#11783A", label: "Validé", Icon: CheckCircle2 },
    rejected: { color: "#D6202C", label: "Refusé", Icon: XCircle },
  }[status] || { color: "#9a9a9a", label: status, Icon: FileText };
  const Icon = meta.Icon;
  return (
    <span
      className="text-xs font-medium px-2.5 py-1 rounded-full inline-flex items-center gap-1"
      style={{ background: meta.color + "1A", color: meta.color }}
    >
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
};
